/**
 * @engine/vfx - Point Map Baker
 * 
 * Utilities for baking 2D images into point maps for VFX particle systems.
 * A point map stores point positions and attributes in texture format,
 * allowing efficient GPU sampling for particle initialization.
 */

/**
 * Point data extracted from source image.
 */
export interface PointData {
  /** Position (x, y normalized to 0-1, z = 0) */
  position: { x: number; y: number; z: number };
  /** RGBA color */
  color: { r: number; g: number; b: number; a: number };
  /** Optional normal (computed from gradient) */
  normal?: { x: number; y: number; z: number };
}

/**
 * Result of baking a point map.
 */
export interface PointMapResult {
  /** Number of valid points */
  pointCount: number;
  /** Position data (Float32Array: xyzw per point) */
  positionData: Float32Array;
  /** Color data (Uint8ClampedArray: rgba per point) */
  colorData: Uint8ClampedArray;
  /** Width of the point map */
  width: number;
  /** Height of the point map */
  height: number;
}

/**
 * Options for baking a point map.
 */
export interface BakePointMapOptions {
  /** Alpha threshold (0-255) for considering a pixel as valid point (default: 1) */
  alphaThreshold?: number;
  /** Scale factor for positions (default: 1) */
  positionScale?: number;
  /** Center the positions around origin (default: true) */
  centerOrigin?: boolean;
  /** Shuffle points for random distribution (default: true) */
  shuffle?: boolean;
}

/**
 * Calculate optimal texture dimensions for storing N points.
 * Returns dimensions that minimize wasted space while being power-of-2 friendly.
 */
function calculateMapDimensions(pointCount: number): { width: number; height: number } {
  if (pointCount === 0) return { width: 1, height: 1 };
  
  // Find smallest power-of-2 that can hold all points
  const sqrt = Math.ceil(Math.sqrt(pointCount));
  
  // Prefer roughly square textures
  let width = 1;
  while (width < sqrt) width *= 2;
  
  let height = Math.ceil(pointCount / width);
  // Round height up to next power of 2 for better GPU compatibility
  let h = 1;
  while (h < height) h *= 2;
  height = h;
  
  return { width, height };
}

/**
 * Extract point data from image pixel data.
 */
function extractPoints(
  imageData: Uint8ClampedArray,
  width: number,
  height: number,
  options: BakePointMapOptions
): PointData[] {
  const { 
    alphaThreshold = 1, 
    positionScale = 1,
    centerOrigin = true,
  } = options;

  const points: PointData[] = [];
  
  // Aspect ratio correction
  const maxDim = Math.max(width, height);
  const aspectX = width / maxDim;
  const aspectY = height / maxDim;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const a = imageData[idx + 3];
      
      if (a >= alphaThreshold) {
        // Normalize position to 0-1 range, then apply scale
        let px = (x / width) * aspectX * positionScale;
        let py = (1 - y / height) * aspectY * positionScale; // Flip Y for standard coordinate system
        
        if (centerOrigin) {
          px -= (aspectX * positionScale) / 2;
          py -= (aspectY * positionScale) / 2;
        }

        points.push({
          position: { x: px, y: py, z: 0 },
          color: {
            r: imageData[idx] / 255,
            g: imageData[idx + 1] / 255,
            b: imageData[idx + 2] / 255,
            a: a / 255,
          },
        });
      }
    }
  }

  return points;
}

/**
 * Shuffle array in-place using Fisher-Yates algorithm.
 */
function shuffleArray<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

/**
 * Create point map data from extracted points.
 */
function createPointMapData(points: PointData[], shouldShuffle: boolean): PointMapResult {
  if (points.length === 0) {
    return {
      pointCount: 0,
      positionData: new Float32Array(4),
      colorData: new Uint8ClampedArray(4),
      width: 1,
      height: 1,
    };
  }

  // Shuffle points for better random distribution
  if (shouldShuffle) {
    points = shuffleArray([...points]);
  }

  const dims = calculateMapDimensions(points.length);
  const totalPixels = dims.width * dims.height;

  // Create position data (RGBA32F: xyz = position, w = 1)
  const positionData = new Float32Array(totalPixels * 4);
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    positionData[i * 4 + 0] = p.position.x;
    positionData[i * 4 + 1] = p.position.y;
    positionData[i * 4 + 2] = p.position.z;
    positionData[i * 4 + 3] = 1.0; // Valid point marker
  }

  // Create color data (RGBA8)
  const colorData = new Uint8ClampedArray(totalPixels * 4);
  for (let i = 0; i < points.length; i++) {
    const c = points[i].color;
    colorData[i * 4 + 0] = Math.round(c.r * 255);
    colorData[i * 4 + 1] = Math.round(c.g * 255);
    colorData[i * 4 + 2] = Math.round(c.b * 255);
    colorData[i * 4 + 3] = Math.round(c.a * 255);
  }

  return {
    pointCount: points.length,
    positionData,
    colorData,
    width: dims.width,
    height: dims.height,
  };
}

/**
 * Bake a point map from an ImageBitmap.
 * Returns raw data arrays that can be uploaded to textures.
 * 
 * @param bitmap - Source image
 * @param options - Bake options
 * @returns Point map data (positions and colors as typed arrays)
 * 
 * @example
 * ```typescript
 * const result = bakePointMapFromBitmap(bitmap, { positionScale: 10 });
 * 
 * // Create position texture (rgba32float)
 * const positionTex = ctx.createTexture({
 *   width: result.width,
 *   height: result.height,
 *   format: 'rgba32float',
 *   usage: ['sampled', 'copy-dst'],
 * });
 * // Write position data to texture...
 * 
 * // Create color texture (rgba8unorm)
 * const colorTex = ctx.createTexture({
 *   width: result.width,
 *   height: result.height,
 *   format: 'rgba8unorm',
 *   usage: ['sampled', 'copy-dst'],
 * });
 * ctx.updateTexture(colorTex, result.colorData);
 * ```
 */
export function bakePointMapFromBitmap(
  bitmap: ImageBitmap,
  options: BakePointMapOptions = {}
): PointMapResult {
  const { shuffle = true } = options;

  // Create offscreen canvas to read pixel data
  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
  const context = canvas.getContext('2d')!;
  context.drawImage(bitmap, 0, 0);
  
  const imageData = context.getImageData(0, 0, bitmap.width, bitmap.height);
  const points = extractPoints(imageData.data, bitmap.width, bitmap.height, options);
  
  return createPointMapData(points, shuffle);
}

/**
 * Bake a point map from a URL.
 * 
 * @param url - Image URL
 * @param options - Bake options
 * @returns Promise resolving to point map data
 */
export async function bakePointMapFromURL(
  url: string,
  options: BakePointMapOptions = {}
): Promise<PointMapResult> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${url} (${response.status})`);
  }
  
  const blob = await response.blob();
  const bitmap = await createImageBitmap(blob);
  
  return bakePointMapFromBitmap(bitmap, options);
}

/**
 * Bake a point map from an HTMLImageElement.
 * 
 * @param image - HTML image element
 * @param options - Bake options
 * @returns Promise resolving to point map data
 */
export async function bakePointMapFromImage(
  image: HTMLImageElement,
  options: BakePointMapOptions = {}
): Promise<PointMapResult> {
  // Ensure image is loaded
  if (!image.complete) {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Failed to load image'));
    });
  }
  
  const bitmap = await createImageBitmap(image);
  return bakePointMapFromBitmap(bitmap, options);
}

/**
 * Bake a point map from raw pixel data.
 * 
 * @param data - RGBA pixel data (Uint8ClampedArray or Uint8Array)
 * @param width - Image width
 * @param height - Image height
 * @param options - Bake options
 * @returns Point map data
 */
export function bakePointMapFromPixelData(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  options: BakePointMapOptions = {}
): PointMapResult {
  const { shuffle = true } = options;
  
  const points = extractPoints(
    data instanceof Uint8ClampedArray ? data : new Uint8ClampedArray(data),
    width,
    height,
    options
  );

  return createPointMapData(points, shuffle);
}
